/**
 * Privacy policy content (Sprint DI-D).
 *
 * Lives here, not in `shared/i18n/messages.ts`, because the
 * privacy text is **legal product copy**, not UI chrome.
 * Reasons:
 *
 *   - The text is paragraph-shaped, not key/label-shaped.
 *     Forcing it into `{ "privacy.section.5.paragraph.3":
 *     "..." }` makes the dictionary 3× longer with keys that
 *     are inferior to the prose for code search.
 *
 *   - The legal-review workflow is distinct from the
 *     translator workflow. When the lawyer revises §5, they
 *     edit one named section here, not a scatter of dotted
 *     keys.
 *
 *   - The audit script (Sprint DJ) excludes `content/legal/`
 *     so legitimate Cyrillic prose doesn't flag as a missed
 *     `useT` call.
 *
 * Bumping a version: keep BOTH `ru` and `en` blocks in sync
 * and update `POLICY_VERSION` in `pages/Privacy.tsx`
 * AND `os-core/api/me.py:PDN_CURRENT_VERSION` (drift-guarded
 * by `test_pdn_versions_match` on the backend).
 *
 * Adding a third language: add a new key to `POLICY_CONTENT`,
 * mirror the section structure, and update the `Lang` type
 * in `shared/i18n/messages.ts` if needed.
 */

import type { Lang } from "../../shared/i18n";


/** One paragraph (`P`) or one bullet list (`UL`). */
export type PolicyNode =
  | { kind: "p";  text: string }
  | { kind: "ul"; items: string[] };

export interface PolicySection {
  heading: string;
  body:    PolicyNode[];
}

export interface PolicyContent {
  /** Title shown above the body, e.g. "Политика обработки …". */
  title:    string;
  /** Format string for the version line. `{version}` is filled. */
  version:  string;
  sections: PolicySection[];
}


// ----- RU ------------------------------------------------------------------


const RU: PolicyContent = {
  title:   "Политика обработки персональных данных",
  version: "Версия {version}",
  sections: [
    {
      heading: "1. Оператор",
      body: [{
        kind: "p",
        text:
          "Оператором обработки персональных данных в системе VSEH AI OS " +
          "выступает компания, развернувшая данный экземпляр платформы " +
          "(ваш работодатель / поставщик услуги). Контактные данные " +
          "оператора доступны в разделе «Настройки → Тенант».",
      }],
    },
    {
      heading: "2. Цели обработки",
      body: [{
        kind: "ul",
        items: [
          "Предоставление операторского и диспетчерского доступа к платформе.",
          "Хранение и обработка переписок с клиентами, ведение CRM.",
          "Публикация и синхронизация объявлений с внешними площадками (Avito, vsteh.ru, Telegram).",
          "Учёт стоимости вызовов AI-моделей и контроль лимитов по тенанту.",
          "Запись действий, существенных для безопасности (audit log).",
        ],
      }],
    },
    {
      heading: "3. Состав обрабатываемых данных",
      body: [{
        kind: "ul",
        items: [
          "Учётная запись: e-mail, полное имя, роль, scope-флаги.",
          "Аутентификационные данные: хэш пароля, секреты MFA (зашифрованы тенант-уникальным ключом).",
          "Telegram chat_id (для уведомлений) — если пользователь подключил бот.",
          "Журнал действий: IP-адрес сессии, User-Agent, время операции, тип события.",
          "Учёт расходов на AI: токены/стоимость/латентность вызовов, привязка к agent_id.",
        ],
      }],
    },
    {
      heading: "4. Правовые основания",
      body: [{
        kind: "p",
        text:
          "Обработка осуществляется на основании согласия субъекта, " +
          "зафиксированного при регистрации (ст. 6 ч. 1 п. 1 Федерального " +
          "закона № 152-ФЗ «О персональных данных»), а также во исполнение " +
          "договора, стороной которого является субъект (ст. 6 ч. 1 п. 5).",
      }],
    },
    {
      heading: "5. Сроки хранения",
      body: [{
        kind: "ul",
        items: [
          "Учётная запись: до отзыва согласия или закрытия аккаунта.",
          "Журнал аудита: настраивается администратором тенанта (по умолчанию 365 дней).",
          "Резервные копии БД: 30 дней с момента создания снимка.",
          "Запрос на удаление: обезличивание через 30 дней — grace-период для отмены.",
        ],
      }],
    },
    {
      heading: "6. Передача третьим лицам",
      body: [{
        kind: "p",
        text:
          "Содержимое переписок с клиентами и тексты карточек объявлений " +
          "могут передаваться внешним AI-провайдерам (OpenAI, Anthropic, " +
          "Ollama) для генерации ответов и эмбеддингов. Учётные данные " +
          "тенанта передаются по защищённому каналу во внешние площадки " +
          "публикации (Avito, vsteh.ru, Telegram) для исполнения операций, " +
          "инициированных пользователем.",
      }],
    },
    {
      heading: "7. Права субъекта",
      body: [{
        kind: "ul",
        items: [
          "Запросить копию хранимых данных (свяжитесь с администратором тенанта).",
          "Отозвать согласие и запросить обезличивание учётной записи (кнопка «Удалить аккаунт» выше).",
          "Уточнить или обновить персональные данные через раздел «Настройки → Профиль».",
          "Обратиться в Роскомнадзор при несогласии с порядком обработки.",
        ],
      }],
    },
    {
      heading: "8. Безопасность",
      body: [{
        kind: "p",
        text:
          "Все секреты (пароли коннекторов, токены ботов, MFA-секреты) " +
          "зашифрованы envelope-схемой AES-256-GCM с per-tenant DEK, " +
          "обёрнутым мастер-ключом (KEK). Доступ к данным разделён по " +
          "уровню тенанта на уровне базы (Postgres Row-Level Security), " +
          "дополнительно гранулируется capability-scope JWT.",
      }],
    },
    {
      heading: "9. Обновление политики",
      body: [{
        kind: "p",
        text:
          "При материальном изменении формулировок версия политики " +
          "будет увеличена; ваша учётная запись потребует подтверждения " +
          "обновлённого текста при следующем входе.",
      }],
    },
  ],
};


// ----- EN ------------------------------------------------------------------


const EN: PolicyContent = {
  title:   "Personal data processing policy",
  version: "Version {version}",
  sections: [
    {
      heading: "1. Operator",
      body: [{
        kind: "p",
        text:
          "The operator processing personal data in VSEH AI OS is the " +
          "organisation that deployed this instance of the platform " +
          "(your employer / service provider). Operator contact details " +
          "are available in «Settings → Tenant».",
      }],
    },
    {
      heading: "2. Processing purposes",
      body: [{
        kind: "ul",
        items: [
          "Providing operator and dispatcher access to the platform.",
          "Storing and processing customer conversations, running CRM.",
          "Publishing and syncing listings to external marketplaces (Avito, vsteh.ru, Telegram).",
          "Tracking AI-model invocation costs and enforcing tenant quotas.",
          "Recording security-relevant actions (audit log).",
        ],
      }],
    },
    {
      heading: "3. Data collected",
      body: [{
        kind: "ul",
        items: [
          "Account: e-mail, full name, role, scope flags.",
          "Authentication data: password hash, MFA secrets (encrypted with a tenant-unique key).",
          "Telegram chat_id (for notifications) — only if the user connected the bot.",
          "Audit log: session IP, User-Agent, operation time, event type.",
          "AI spend telemetry: tokens / cost / latency per call, agent_id binding.",
        ],
      }],
    },
    {
      heading: "4. Legal basis",
      body: [{
        kind: "p",
        text:
          "Processing is performed on the basis of the data subject's " +
          "consent given at registration (Art. 6.1.1 of Russian Federal " +
          "Law 152-ФЗ «On Personal Data») and in performance of a " +
          "contract to which the subject is a party (Art. 6.1.5).",
      }],
    },
    {
      heading: "5. Retention periods",
      body: [{
        kind: "ul",
        items: [
          "Account: until consent withdrawal or account closure.",
          "Audit log: configured by the tenant admin (default 365 days).",
          "DB backups: 30 days from snapshot creation.",
          "Deletion request: data is depersonalised after 30 days — grace window for cancellation.",
        ],
      }],
    },
    {
      heading: "6. Third-party transfers",
      body: [{
        kind: "p",
        text:
          "Customer conversation content and listing-card text may be " +
          "transferred to external AI providers (OpenAI, Anthropic, " +
          "Ollama) for reply generation and embeddings. Tenant " +
          "credentials are transferred over a secure channel to external " +
          "publishing platforms (Avito, vsteh.ru, Telegram) to execute " +
          "user-initiated operations.",
      }],
    },
    {
      heading: "7. Subject rights",
      body: [{
        kind: "ul",
        items: [
          "Request a copy of stored data (contact the tenant administrator).",
          "Withdraw consent and request account depersonalisation (\"Delete account\" button above).",
          "Update or correct personal data via «Settings → Profile».",
          "File a complaint with Roskomnadzor if you disagree with the processing.",
        ],
      }],
    },
    {
      heading: "8. Security",
      body: [{
        kind: "p",
        text:
          "All secrets (connector passwords, bot tokens, MFA secrets) " +
          "are encrypted with an AES-256-GCM envelope scheme using a " +
          "per-tenant DEK wrapped by a master key (KEK). Tenant-level " +
          "data isolation is enforced at the DB layer (Postgres " +
          "Row-Level Security) and further constrained by " +
          "capability-scope JWTs.",
      }],
    },
    {
      heading: "9. Policy updates",
      body: [{
        kind: "p",
        text:
          "When the wording changes materially the policy version is " +
          "incremented; your account will require re-confirmation of " +
          "the updated text on the next sign-in.",
      }],
    },
  ],
};


export const POLICY_CONTENT: Record<Lang, PolicyContent> = { ru: RU, en: EN };
